import { Alert, CircularProgress, Skeleton, Stack } from "@mui/material";
import { useDeepCompareEffect } from "@react-hookz/web";
import { useReducer, type FC } from "react";
import { extractZodTreeErrorProps } from "../../../utils/errors/extract-zod-tree-error-props";
import { FixedFooter } from "../../generic/components/fixed-footer";
import { PageWithFooterWrapper } from "../../generic/components/page-with-footer-wrapper";
import { MomentLogV2 } from "../../moment/component/audit/moment-log-v2";
import {
  useFollowUpMomentDetails,
  type FollowUpMomentDetailsConfig,
} from "../hooks/use-follow-up-moment-details";
import { followUpReducer, makeDefaultFollowUpState } from "../reducer/reducer";
import type { FollowUpChannelWithData, FollowUpSchemaType } from "../reducer/schema";
import { FollowUpBody } from "./follow-up-body/follow-up-body";
import { FollowUpBodySection } from "./follow-up-body/follow-up-body-section";
import { FollowUpArchiveButton } from "./follow-up-buttons/follow-up-archive-button";
import { FollowUpDebugButton } from "./follow-up-buttons/follow-up-debug-button";
import { FollowUpPauseButton } from "./follow-up-buttons/follow-up-pause-button";
import { FollowUpSaveButtons } from "./follow-up-buttons/follow-up-save-buttons";
import { FollowUpChannelSelect } from "./follow-up-channel-select/follow-up-channel-select";
import { FollowUpHeader } from "./follow-up-header";
import { FollowUpMessage } from "./follow-up-message/follow-up-message";
import { FollowUpRecurringWarning } from "./follow-up-recurring-warning";
import { FollowUpScheduleFinished } from "./follow-up-schedule/follow-up-schedule-finished";
import { FollowUpScheduleScheduled } from "./follow-up-schedule/follow-up-schedule-scheduled";
import { FollowUpSidebar } from "./follow-up-sidebar";

export const FollowUpWrapper: FC<{ config: FollowUpMomentDetailsConfig }> = ({ config }) => {
  const loadState = useFollowUpMomentDetails(config);

  const [state, dispatch] = useReducer(followUpReducer, makeDefaultFollowUpState(config));

  useDeepCompareEffect(() => {
    if (loadState.loading) dispatch({ type: "LOAD_START" });
    else if (loadState.error) dispatch({ type: "LOAD_ERROR", error: loadState.error });
    else if (loadState.data) dispatch({ type: "LOAD_SUCCESS", payload: loadState.data });
  }, [loadState]);

  return (
    <>
      <FollowUpHeader
        loading={state.momentDetails.loading}
        programTitle={state.momentDetails.data?.momentFollowUpDetails.program?.title ?? undefined}
        title={state.ui.title}
        status={state.momentDetails.data?.followUp?.status ?? "draft"}
        setTitle={(title: string) => dispatch({ type: "SET_TITLE", payload: title })}
        error={extractZodTreeErrorProps(state.errors, ["title"])}
        disabled={state.derivedData.uiDisabled.form}
      >
        <Stack direction="row" spacing={0.5}>
          {state.derivedData.saveData?.id && (
            <FollowUpPauseButton
              moment_id={state.derivedData.saveData.id}
              saving={state.saveState.saving}
              onSaving={() => dispatch({ type: "SET_SAVING" })}
              onSaved={(success) =>
                success
                  ? dispatch({ type: "FINISHED_PAUSE" })
                  : dispatch({ type: "FINISHED_SAVING", payload: "Unable to Pause" })
              }
              disabled={state.derivedData.uiDisabled.buttons.pause}
            />
          )}
          {state.momentDetails.data?.followUp && (
            <MomentLogV2
              moment={{
                id: state.momentDetails.data.followUp.moment_id,
                source_moment_id: null,
                discover_moment_launched_from: null,
              }}
              showText={false}
            />
          )}
          {state.momentDetails.data && (
            <FollowUpArchiveButton
              momentDetails={state.momentDetails.data}
              handleArchiveStatusChange={(archived: boolean) =>
                dispatch({ type: "ARCHIVE_STATUS_CHANGED", payload: archived })
              }
              isDirty={state.derivedData.isDirty}
            />
          )}
          <FollowUpDebugButton state={state} />
        </Stack>
      </FollowUpHeader>

      <PageWithFooterWrapper>
        <FollowUpSidebar
          followUpMomentDetailsState={state.momentDetails}
          reason={state.ui.reason}
          setReason={(reason) => dispatch({ type: "SET_REASON", payload: reason })}
          error={extractZodTreeErrorProps(state.errors, ["reason"])}
          disabled={state.derivedData.uiDisabled.form}
        />
        <FollowUpBody>
          {state.momentDetails.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              There was an error loading the Follow-Up details.
            </Alert>
          )}

          <FollowUpRecurringWarning
            details={state.momentDetails.data?.momentFollowUpDetails || null}
          />

          {!state.momentDetails.error && (
            <>
              {state.momentDetails.loading ? (
                <Skeleton variant="rounded" width="100%" height={275} animation="wave" />
              ) : (
                <FollowUpChannelSelect
                  enabledChannels={state.momentDetails.data?.enabledChannels || []}
                  selectedChannel={state.ui.channel}
                  setChannel={(payload) => dispatch({ type: "SET_CHANNEL", payload })}
                  segmentPersons={state.momentDetails.data?.segmentPersons || []}
                  error={extractZodTreeErrorProps(state.errors, ["channel"])}
                  disabled={state.derivedData.uiDisabled.form}
                />
              )}

              {state.momentDetails.loading ? (
                <Skeleton variant="rounded" width="100%" height={275} animation="wave" />
              ) : (
                <FollowUpBodySection
                  title="Schedule"
                  subtitle={scheduleSubtitleMessage(state.ui.mode)}
                >
                  {state.ui.mode === "finished" && (
                    <FollowUpScheduleFinished
                      value={state.ui.schedule}
                      onChange={(schedule) =>
                        dispatch({ type: "SET_SCHEDULE", payload: { mode: "finished", schedule } })
                      }
                      errors={{
                        schedule_type: extractZodTreeErrorProps(state.errors, ["schedule", "type"])
                          .helperText,
                        date: extractZodTreeErrorProps(state.errors, ["schedule", "date"])
                          .helperText,
                        time: extractZodTreeErrorProps(state.errors, ["schedule", "time"])
                          .helperText,
                        timezone: extractZodTreeErrorProps(state.errors, ["schedule", "timezone"])
                          .helperText,
                      }}
                      disabled={state.derivedData.uiDisabled.form}
                    />
                  )}
                  {state.ui.mode === "scheduled" && (
                    <FollowUpScheduleScheduled
                      value={state.ui.schedule}
                      onChange={(schedule) =>
                        dispatch({ type: "SET_SCHEDULE", payload: { mode: "scheduled", schedule } })
                      }
                      errors={{
                        amount: extractZodTreeErrorProps(state.errors, ["schedule", "amount"])
                          .helperText,
                        period: extractZodTreeErrorProps(state.errors, ["schedule", "period"])
                          .helperText,
                        time: extractZodTreeErrorProps(state.errors, ["schedule", "time"])
                          .helperText,
                        timezone: extractZodTreeErrorProps(state.errors, ["schedule", "timezone"])
                          .helperText,
                      }}
                      disabled={state.derivedData.uiDisabled.form}
                    />
                  )}
                </FollowUpBodySection>
              )}

              {state.momentDetails.loading || !state.derivedData.saveData ? (
                <Skeleton variant="rounded" width="100%" height={400} animation="wave" />
              ) : (
                <FollowUpMessage
                  original_moment_id={state.momentDetails.data?.momentFollowUpDetails.id ?? null}
                  moment_id={state.derivedData.saveData.id}
                  channel={
                    {
                      channel: state.ui.channel,
                      channelData: state.ui.channelData,
                    } as FollowUpChannelWithData
                  }
                  setChannelData={(payload) => dispatch({ type: "SET_CHANNEL", payload })}
                  mergeTags={state.momentDetails.data?.mergeTags || []}
                  toTarget={state.derivedData.toTarget}
                  setDesign={(payload) => dispatch({ type: "SET_MESSAGE_DESIGN", payload })}
                  design={state.ui.design}
                  restrictedEmailFrom={state.momentDetails.data?.restrictions.email_from ?? []}
                  errors={{
                    slack_alias_id: extractZodTreeErrorProps(state.errors, [
                      "channelData",
                      "slack_alias_id",
                    ]).helperText,
                    design: extractZodTreeErrorProps(state.errors, ["design"]).helperText,
                    email_sender: extractZodTreeErrorProps(state.errors, [
                      "channelData",
                      "email_sender",
                    ]).helperText,
                    email_subject: extractZodTreeErrorProps(state.errors, [
                      "channelData",
                      "email_subject",
                    ]).helperText,
                  }}
                  disabled={state.derivedData.uiDisabled.form}
                />
              )}
            </>
          )}
        </FollowUpBody>
        <FixedFooter noSidebar>
          <Stack
            direction="row-reverse"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 2 }}
          >
            {state.derivedData.saveData && state.momentDetails.data?.momentFollowUpDetails ? (
              <FollowUpSaveButtons
                saveData={state.derivedData.saveData}
                errors={state.errors}
                usesRestrictedData={state.derivedData.usesRestrictedData}
                uiData={state.ui}
                moment={state.momentDetails.data.momentFollowUpDetails}
                saving={state.saveState.saving}
                onSaving={() => dispatch({ type: "SET_SAVING" })}
                onSaved={(payload) => dispatch({ type: "FINISHED_SAVING", payload })}
                onPublished={() => dispatch({ type: "FINISHED_PUBLISH" })}
                showErrors={() => dispatch({ type: "SET_ERRORS_DISPLAY", payload: true })}
                disabled={state.derivedData.uiDisabled.buttons}
              />
            ) : (
              <CircularProgress />
            )}
          </Stack>
        </FixedFooter>
      </PageWithFooterWrapper>
    </>
  );
};

function scheduleSubtitleMessage(mode: FollowUpSchemaType["mode"]): string {
  switch (mode) {
    case "finished":
      return "When do you want to send the Follow-Up?";
    case "scheduled":
      return `Choose how long to wait after the recipient receives the message before sending the Follow-Up. If you change the send date of the original message later, the Follow-Up will still send after the period you define here.`;
  }
}
